<template>
	<div class="min-h-[370px] max-w-[580px]">
		<div
			v-if="isCartEmpty"
			class="px-4 text-xs font-semibold text-slate-600!"
		>
			{{ t('document.noItemsInCart') }}
		</div>
		<template v-else-if="hasBirthDate && selectedPlan">
			<div class="grid grid-cols-2 gap-[18px]">
				<FormKit
					type="number"
					name="funeralAmount"
					v-model="funeralAmountInput"
					number
					:label="t('payment.totalFuneralAmount')"
					:step="100"
					input-class="font-normal"
					outer-class="mb-0! max-w-full"
					wrapper-class="mb-0!"
					messages-class="font-medium"
					:validation="[['funeralAmountRule']]"
					:validation-rules="{ funeralAmountRule }"
					:validation-messages="{ funeralAmountRule: '' }"
					validation-visibility="blur"
					@blur="paymentOptions?.handleFuneralAmountBlur?.()"
				/>
				<!-- Down Payment -->
				<FormKit
					id="downPayment"
					v-model="stagedDownPayment"
					type="number"
					name="downPayment"
					:label="t('payment.downPayment')"
					:step="100"
					number
					:validation="[['downPaymentRule']]"
					:validation-rules="{ downPaymentRule }"
					:validation-messages="{ downPaymentRule: '' }"
					validation-visibility="blur"
					:disabled="isDownPaymentDisabled"
					input-class="font-normal"
					outer-class="mb-0! max-w-full"
					messages-class="pt-2 font-medium"
					wrapper-class="mb-0!"
					@blur="handleDownPaymentBlur"
				/>
			</div>
			<div class="grid grid-cols-2 gap-[18px] pt-4">
				<div class="flex flex-col gap-2">
					<!-- Payment Type -->
					<FormKit
						type="select"
						:label="t('payment.paymentType')"
						:placeholder="
							t('plan.placeholders.select', {
								placeholderLabel: t('payment.paymentType'),
							})
						"
						v-model="stagedPaymentType"
						:options="paymentTypeOptions"
						input-class="font-normal"
						outer-class="mb-0! max-w-full"
						wrapper-class="mb-0!"
					/>

					<!-- Payment Frequency -->
					<FormKit
						type="select"
						:label="t('payment.paymentFrequency')"
						:placeholder="
							t('plan.placeholders.select', {
								placeholderLabel: t('payment.paymentFrequency'),
							})
						"
						:disabled="isPaymentFrequencyDisabled"
						v-model="stagedPaymentFrequency"
						:options="paymentFrequencyOptions"
						input-class="font-normal"
						outer-class="mb-0! max-w-full"
						wrapper-class="mb-0!"
					/>

					<!-- OAP Payment Frequency -->
					<FormKit
						type="select"
						:label="t('payment.oapPaymentFrequency')"
						:placeholder="
							t('plan.placeholders.select', {
								placeholderLabel: t('payment.paymentFrequency'),
							})
						"
						v-model="oapPaymentCount"
						:options="oapPaymentFrequencyOptions"
						input-class="font-normal"
						outer-class="mb-0! max-w-full"
						wrapper-class="mb-0!"
					/>
				</div>
				<!-- Payment Term Options -->
				<RadioGroup
					v-if="paymentOptions?.paymentTermOptions?.value?.termOptions.length"
					v-model="stagedInsuranceTerm"
					class="flex flex-col justify-between"
				>
					<RadioGroupLabel class="text-sm font-semibold text-slate-900!">
						{{ t('document.changePaymentDetailsModal.paymentTerm') }}
					</RadioGroupLabel>
					<RadioGroupOption
						v-for="option in termOptions"
						:key="option.term"
						:value="option.term"
						v-slot="{ checked }"
						:disabled="isPaymentTermDisabled(option.term)"
					>
						<div
							class="flex w-full cursor-pointer justify-between rounded-lg border-2 bg-white p-3 shadow-md"
							:class="[
								checked ? 'border-accent-500!' : 'border-transparent!',
								isPaymentTermDisabled(option.term)
									? 'pointer-events-none cursor-not-allowed opacity-50'
									: '',
							]"
						>
							<div class="flex items-center gap-3">
								<div
									class="flex h-5 w-5 items-center justify-center rounded border"
									:class="
										checked
											? 'border-accent-400! bg-white'
											: 'border-gray-300! bg-white'
									"
								>
									<div
										v-if="checked"
										class="bg-accent-400! h-3 w-3 rounded-sm"
									></div>
								</div>
								<span class="text-sm font-semibold text-slate-900!">{{
									option.label
								}}</span>
							</div>
							<div class="text-sm text-slate-500!">
								{{ option.formattedPayment }}
							</div>
						</div>
					</RadioGroupOption>
				</RadioGroup>
			</div>
		</template>
		<div
			v-else
			class="px-4 text-xs font-semibold text-slate-600!"
		>
			{{ t('document.noBirthDate') }}
		</div>
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { useOapPlanProvider } from '@/mixins/oapPlanProvider'
import { usePaymentOptions } from '@/mixins/paymentOptions'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import type { PlanData } from '@/types'
import type { FormKitNode } from '@formkit/core'
import { FormKit } from '@formkit/vue'
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue'
import { PaymentType, Frequency, Term } from '@precoa/calculator-sdk'
import { computed, ref, shallowRef, watch } from 'vue'

const sessionStore = useSessionStore()
const planStore = usePlanStore()
const { getPlanCartItems, calculateCartTotals } = useMerchandise()
const { getPlanPaymentOptions } = usePaymentOptions()
const { getPlanById, updateOapPaymentCount } = useOapPlanProvider(
	sessionStore.activeFh?.oap?.data.carrier ?? null
)
const { t } = usePresentationLocale()

const props = defineProps<{ selectedPlanId: string }>()

// Staged changes that will be committed only when confirm is clicked
const stagedChanges = defineModel<Partial<PlanData>>('stagedChanges', {
	required: true,
})

// Snapshot of the plan state when component is initialized(for reset)
const initialPlanSnapshot = ref<{
	insurance: PlanData['insurance']
	oapPayment: PlanData['oapPayment']
} | null>(null)

// Always select the matching plan, or default to the first one
const selectedPlan = computed(
	() => planStore.getPlan(props.selectedPlanId) as Plan
)

// Check if the cart is empty
const isCartEmpty = computed(() => {
	if (!selectedPlan.value) return true
	const cartItems = getPlanCartItems(selectedPlan.value.id) ?? []
	const hasCartItems = cartItems.length > 0
	const hasCustomItems = (selectedPlan.value.customCartItems ?? []).length > 0
	return !hasCartItems && !hasCustomItems
})

// OAP payment count - staged separately since it affects oapPayment object
const oapPaymentCount = ref<number>(
	getPlanById(selectedPlan.value?.oapPayment.paymentPlanId)?.paymentCount ?? 1
)

// Computed property for staged insurance term
const stagedInsuranceTerm = computed({
	get: () => {
		return (
			(stagedChanges.value.insurance
				?.term as typeof selectedPlan.value.insurance.term) ??
			selectedPlan.value.insurance.term
		)
	},
	set: (value) => {
		if (!stagedChanges.value.insurance) {
			// Initialize with current plan values to satisfy required fields
			stagedChanges.value.insurance = {
				...selectedPlan.value.insurance,
			}
		}
		stagedChanges.value.insurance.term = value
	},
})

// Computed property for staged payment type
const stagedPaymentType = computed({
	get: () => {
		return (
			(stagedChanges.value.insurance
				?.paymentType as typeof selectedPlan.value.insurance.paymentType) ??
			selectedPlan.value.insurance.paymentType
		)
	},
	set: (value) => {
		if (!stagedChanges.value.insurance) {
			// Initialize with current plan values to satisfy required fields
			stagedChanges.value.insurance = {
				...selectedPlan.value.insurance,
			}
		}
		stagedChanges.value.insurance.paymentType = value
	},
})

// Computed property for staged payment frequency
const stagedPaymentFrequency = computed({
	get: () => {
		return (
			(stagedChanges.value.insurance
				?.paymentFrequency as typeof selectedPlan.value.insurance.paymentFrequency) ??
			selectedPlan.value.insurance.paymentFrequency
		)
	},
	set: (value) => {
		if (!stagedChanges.value.insurance) {
			// Initialize with current plan values to satisfy required fields
			stagedChanges.value.insurance = {
				...selectedPlan.value.insurance,
			}
		}
		stagedChanges.value.insurance.paymentFrequency = value
	},
})

// Computed property for staged down payment
const stagedDownPayment = computed({
	get: () => {
		return (
			stagedChanges.value.insurance?.downPayment ??
			selectedPlan.value.insurance.downPayment
		)
	},
	set: (value) => {
		if (!stagedChanges.value.insurance) {
			// Initialize with current plan values to satisfy required fields
			stagedChanges.value.insurance = {
				...selectedPlan.value.insurance,
			}
		}
		stagedChanges.value.insurance.downPayment = value
	},
})

// Create a computed plan that merges staged changes with the actual plan
// This is used for paymentOptions calculations
const stagedPlan = computed(() => {
	if (!selectedPlan.value) return null

	// Create a shallow copy and merge staged changes
	const plan = { ...selectedPlan.value }

	if (stagedChanges.value.insurance) {
		plan.insurance = {
			...plan.insurance,
			...stagedChanges.value.insurance,
		}
	}

	if (stagedChanges.value.oapPayment) {
		plan.oapPayment = stagedChanges.value.oapPayment
	}

	return plan as Plan
})

// If the user changes the funeral amount update it
const funeralAmountInput = computed({
	get: () => paymentOptions.value?.funeralAmountInput?.value,
	set: (val) => {
		if (paymentOptions.value?.funeralAmountInput) {
			paymentOptions.value.funeralAmountInput.value = val
		}
	},
})

// The payment term options
const termOptions = computed(
	() => paymentOptions.value?.paymentTermOptions.value.termOptions ?? []
)

// The payment type options
const paymentTypeOptions = computed(
	() => paymentOptions.value?.paymentTypeOptions.value ?? []
)

// The payment frequency options
const paymentFrequencyOptions = computed(
	() => paymentOptions.value?.paymentFrequencyOptions.value ?? []
)

// The OAP payment frequency options
const oapPaymentFrequencyOptions = computed(
	() => paymentOptions.value?.oapPaymentFrequencyOptions.value ?? []
)

// Check if the selected plan has a birth date
const hasBirthDate = computed(() => !!selectedPlan.value?.birthDate)

// Check if the down payment is disabled based on term, payment type, and funeral amount
const isDownPaymentDisabled = computed(() => {
	const paymentType = stagedPlan.value?.insurance?.paymentType
	const term = stagedPlan.value?.insurance?.term
	const funeralAmount =
		paymentOptions.value?.funeralAmountInput?.value ??
		paymentOptions.value?.baseAmount.value ??
		0

	if (funeralAmount < 1000) return true

	return paymentType === PaymentType.SP_EFT || term === Term.SinglePay
})

// Check if the payment frequency is disabled (e.g., Single Pay term)
const isPaymentFrequencyDisabled = computed(
	() =>
		stagedPaymentType.value === PaymentType.SP_EFT ||
		stagedPaymentType.value === PaymentType.MP_EFT ||
		(stagedPaymentType.value === PaymentType.CREDIT_CARD &&
			!sessionStore.activeFh?.isAllowedToSellSinglePayCc)
)

/**
 * Use shallowRef to preserve reactivity of localFuneralAmount and other internal refs.
 * Regular ref would unwrap, breaking reactivity
 */
const paymentOptions = shallowRef(getPlanPaymentOptions(selectedPlan.value))

// Initialize snapshot when plan is first loaded
watch(
	selectedPlan,
	(plan) => {
		if (!plan || initialPlanSnapshot.value) return
		initialPlanSnapshot.value = {
			insurance: {
				...plan.insurance,
			},
			oapPayment: { ...plan.oapPayment },
		}
	},
	{ immediate: true }
)

// Update the staged payment frequency and term based on the staged payment type
watch(stagedPaymentType, (newPaymentType) => {
	if (newPaymentType === PaymentType.SP_EFT) {
		stagedPaymentFrequency.value = Frequency.OneTime
		stagedInsuranceTerm.value = Term.SinglePay
	} else if (newPaymentType === PaymentType.MP_EFT) {
		stagedPaymentFrequency.value = Frequency.Monthly
		stagedInsuranceTerm.value = Term.TenYear
	} else if (newPaymentType === PaymentType.CREDIT_CARD) {
		if (!sessionStore.activeFh?.isAllowedToSellSinglePayCc) {
			stagedPaymentFrequency.value = Frequency.Monthly
			stagedInsuranceTerm.value = Term.TenYear
		} else {
			stagedPaymentFrequency.value = Frequency.OneTime
			stagedInsuranceTerm.value = Term.SinglePay
		}
	} else if (newPaymentType === PaymentType.BILL) {
		stagedPaymentFrequency.value = Frequency.Monthly
		stagedInsuranceTerm.value = Term.TenYear
	}
})

// Update the staged insurance term based on the staged payment frequency
watch(
	() => stagedPaymentFrequency.value,
	(newFrequency) => {
		if (
			newFrequency === Frequency.OneTime &&
			stagedPaymentType.value === PaymentType.CREDIT_CARD
		) {
			stagedInsuranceTerm.value = Term.SinglePay
		} else if (
			stagedPaymentType.value === PaymentType.CREDIT_CARD &&
			stagedInsuranceTerm.value === Term.SinglePay &&
			newFrequency !== Frequency.OneTime
		) {
			stagedInsuranceTerm.value = Term.TenYear
		}
	}
)

// Watch oapPaymentCount and stage the change instead of committing
watch(oapPaymentCount, (newCount) => {
	const oapPayment = updateOapPaymentCount(
		selectedPlan.value.oapPayment,
		newCount
	)

	if (!oapPayment) {
		return
	}

	// Stage the change instead of committing
	stagedChanges.value.oapPayment = oapPayment
})

/**
 * Watch for changes to the selected plan and re-initialize the payment options accordingly.
 *
 * This ensures that whenever the plan changes (e.g., new tab selected, ID changes), we get a fresh copy
 * If a previous local funeral amount override was set, carry it over into the new options object.
 * This avoids resetting user input when reloading data (like adding a down payment or changing terms)
 */
watch(
	[selectedPlan, stagedPlan],
	([plan, staged]) => {
		if (!plan || !staged) return
		const newOptions = getPlanPaymentOptions(staged)

		// update baseAmount and localFuneralAmount manually here
		newOptions.baseAmount.value = calculateCartTotals(staged).funeralTotal
		newOptions.localFuneralAmount.value = newOptions.baseAmount.value

		paymentOptions.value = newOptions
	},
	{ immediate: true }
)

// On blur of the down payment field, set the amount to be something valid
const handleDownPaymentBlur = () =>
	paymentOptions.value?.handleDownPaymentBlur?.()

// Make sure funeral amount is valid based on calculator sdk, if not show error
const funeralAmountRule = (node: FormKitNode) =>
	paymentOptions.value?.funeralAmountRule?.(node) ?? true

// Make sure down payment is valid based on calculator sdk, if not show error
const downPaymentRule = (node: FormKitNode) =>
	paymentOptions.value?.downPaymentRule?.(node) ?? true

// Check if the payment term is disabled based on payment type and frequency, and user's ability to sell single pay credit card plans
const isPaymentTermDisabled = (term: Term) => {
	if (stagedPaymentType.value === PaymentType.SP_EFT) {
		return term !== Term.SinglePay
	}

	if (stagedPaymentType.value === PaymentType.MP_EFT) {
		return term === Term.SinglePay
	}

	if (stagedPaymentType.value === PaymentType.BILL) {
		return term === Term.SinglePay
	}

	if (stagedPaymentType.value === PaymentType.CREDIT_CARD) {
		if (term === Term.SinglePay) {
			if (!sessionStore.activeFh?.isAllowedToSellSinglePayCc) {
				return true
			}

			return stagedPaymentFrequency.value === Frequency.Monthly
		}

		return stagedPaymentFrequency.value === Frequency.OneTime
	}

	return false
}
</script>
